import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  type BoardAccess,
  type Participant,
  type Translate,
  permissionLabel,
  useSetUserExceptions,
} from '@/entities/collab';
import { FormError } from '@/shared/forms';
import { BOARD_SCOPE_PERMISSIONS } from '@/shared/lib/permissions';
import { Button, Combobox, Dialog } from '@/shared/ui';

import styles from './BoardAccess.module.css';

type Choice = 'inherit' | 'allow' | 'deny';

/** Individual exceptions of one participant: per permission inherit / allow / deny. */
export function ExceptionsDialog({
  boardId,
  person,
  current,
  onClose,
}: {
  boardId: string;
  person: Participant;
  current: BoardAccess['exceptions'][number] | undefined;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const tr = t as unknown as Translate;
  const save = useSetUserExceptions(boardId);
  const [choices, setChoices] = useState<Record<string, Choice>>(() => {
    const initial: Record<string, Choice> = {};
    for (const p of current?.granted ?? []) initial[p] = 'allow';
    for (const p of current?.revoked ?? []) initial[p] = 'deny';
    return initial;
  });
  const submit = () => {
    const granted = Object.entries(choices)
      .filter(([, c]) => c === 'allow')
      .map(([p]) => p);
    const revoked = Object.entries(choices)
      .filter(([, c]) => c === 'deny')
      .map(([p]) => p);
    save.mutate({ userId: person.user_id, granted, revoked }, { onSuccess: onClose });
  };
  return (
    <Dialog
      title={t('access.exceptionsTitle', { name: person.display_name })}
      size="lg"
      onClose={onClose}
    >
      <p className={styles.sub}>{t('access.exceptionsHint')}</p>
      <div className={styles.body}>
        {BOARD_SCOPE_PERMISSIONS.filter((p) => p !== 'board.view' && p !== 'board.transfer').map(
          (permission) => (
            <div key={permission} className={styles.exceptionRow}>
              <span>{permissionLabel(tr, permission)}</span>
              <Combobox
                label={permissionLabel(tr, permission)}
                value={choices[permission] ?? 'inherit'}
                size="sm"
                options={[
                  { value: 'inherit', label: t('access.exceptionInherit') },
                  { value: 'allow', label: t('access.exceptionAllow') },
                  { value: 'deny', label: t('access.exceptionDeny') },
                ]}
                onChange={(next) => {
                  setChoices((c) => ({ ...c, [permission]: next as Choice }));
                }}
              />
            </div>
          ),
        )}
        <FormError error={save.error} />
        <div className={styles.actions}>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="primary" disabled={save.isPending} onClick={submit}>
            {t('common.save')}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
